export type JournalDraftKind = 'new' | 'appointment' | 'entry' | 'amendment' | 'amendment-new';

export type JournalDraftScope =
  | { kind: 'new' }
  | { kind: 'appointment'; appointmentId: string }
  | { kind: 'entry'; entryId: string }
  | { kind: 'amendment'; amendmentId: string }
  | { kind: 'amendment-new'; entryId: string };

export interface JournalDraftPayload {
  title: string;
  content_md: string;
  content_html: string | null;
  updated_at: string;
}

export function journalDraftScopeKey(scope: JournalDraftScope): string {
  switch (scope.kind) {
    case 'new':
      return 'new';
    case 'appointment':
      return `appointment:${scope.appointmentId}`;
    case 'entry':
      return `entry:${scope.entryId}`;
    case 'amendment':
      return `amendment:${scope.amendmentId}`;
    case 'amendment-new':
      return `amendment-new:${scope.entryId}`;
    default: {
      const _exhaustive: never = scope;
      return _exhaustive;
    }
  }
}

/**
 * localStorage key for a writing draft. Uses user/patient/scope ids only — never CPR.
 */
export function journalDraftStorageKey(
  userId: string,
  patientId: string,
  scopeKey: string,
): string {
  return `clinio_journal_draft:${userId}:${patientId}:${scopeKey}`;
}

export function readJournalDraftLocal(
  userId: string,
  patientId: string,
  scopeKey: string,
): JournalDraftPayload | null {
  try {
    const raw = localStorage.getItem(journalDraftStorageKey(userId, patientId, scopeKey));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const row = parsed as Partial<JournalDraftPayload>;
    if (typeof row.content_md !== 'string') return null;
    return {
      title: typeof row.title === 'string' ? row.title : '',
      content_md: row.content_md,
      content_html: typeof row.content_html === 'string' ? row.content_html : null,
      updated_at: typeof row.updated_at === 'string' ? row.updated_at : new Date(0).toISOString(),
    };
  } catch {
    return null;
  }
}

export function writeJournalDraftLocal(
  userId: string,
  patientId: string,
  scopeKey: string,
  payload: JournalDraftPayload,
): void {
  try {
    localStorage.setItem(
      journalDraftStorageKey(userId, patientId, scopeKey),
      JSON.stringify(payload),
    );
  } catch {
    // Quota / private mode — server save still retries.
  }
}

export function clearJournalDraftLocal(userId: string, patientId: string, scopeKey: string): void {
  try {
    localStorage.removeItem(journalDraftStorageKey(userId, patientId, scopeKey));
  } catch {
    // ignore
  }
}

export function pickNewerJournalDraft(
  a: JournalDraftPayload | null,
  b: JournalDraftPayload | null,
): JournalDraftPayload | null {
  if (!a) return b;
  if (!b) return a;
  return Date.parse(a.updated_at) >= Date.parse(b.updated_at) ? a : b;
}

export function journalDraftHasContent(payload: {
  title?: string | null;
  content_md?: string | null;
}): boolean {
  return Boolean((payload.title ?? '').trim() || (payload.content_md ?? '').trim());
}

/** Persist only after the user changed the title or wrote body text. */
export function journalDraftHasUserContent(
  payload: { title?: string | null; content_md?: string | null; contentMd?: string | null },
  baselineTitle = '',
): boolean {
  if ((payload.content_md ?? payload.contentMd ?? '').trim()) return true;
  const title = (payload.title ?? '').trim();
  return Boolean(title && title !== baselineTitle.trim());
}
